import { useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * Lightweight client (customer organization) options for filter dropdowns.
 *
 * Fetches a page of clients from GET /admin/clients once and maps them to
 * `{ id, name }` for a searchable SelectField. Unlike `useClients`, it does NOT
 * couple to the URL search params (that hook drives the Clients list page),
 * so it's safe to mount inside the inbox filter panel. Fails silently to an
 * empty list if the user lacks clients:view.
 */
export interface ClientOption {
  id: string
  name: string
}

interface ClientRow {
  id: string
  full_name: string | null
  email?: string | null
}

export function useClientOptions(): { clients: ClientOption[]; loading: boolean } {
  const [clients, setClients] = useState<ClientOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<{ clients: ClientRow[] }>('/admin/clients?limit=500&sortBy=full_name&sortDir=asc')
        if (cancelled) return
        setClients(
          (res.clients ?? []).map((c) => ({
            id: c.id,
            name: c.full_name?.trim() || c.email?.trim() || 'Unnamed client',
          })),
        )
      } catch {
        if (!cancelled) setClients([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { clients, loading }
}
